/* eslint-disable react/require-default-props */
/* eslint-disable max-lines-per-function */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */

'use client';

import type React from 'react';

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import {
  animate, motion, type PanInfo, useMotionValue, useTransform,
} from 'framer-motion';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => { return window.removeEventListener('resize', checkIsMobile); };
  }, []);

  return isMobile;
};

export interface HCarousel3DProps {
  items: React.ReactNode[]
  baseWidth?: number
  gap?: number
  blur?: boolean
  animated?: boolean
  autoPlay?: boolean
  enable3D?: boolean
  currentIndex?: number
  onIndexChange?: (index: number) => void
  opts?: {
    align?: 'start' | 'center' | 'end'
    mobileAlign?: 'start' | 'center' | 'end'
    startIndex?: number
    duration?: number
    active?: boolean
  }
}

const HCarousel3D = ({
  items,
  baseWidth = 316,
  gap = 20,
  blur = false,
  animated = false,
  autoPlay = false,
  enable3D = true,
  currentIndex: externalCurrentIndex,
  onIndexChange,
  opts = {
    align: 'center',
    mobileAlign: 'start',
    startIndex: 0,
    duration: 0.3,
    active: true,
  },
}: HCarousel3DProps) => {
  const isMobile = useIsMobile();
  const [internalCurrentIndex, setInternalCurrentIndex] = useState(opts.startIndex || 0);
  const currentIndex = externalCurrentIndex !== undefined ? externalCurrentIndex : internalCurrentIndex;
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(baseWidth);
  const [itemsPerView, setItemsPerView] = useState(1);
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const x = useMotionValue(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const trackItemOffset = itemWidth + gap;

  const setCurrentIndex = useCallback(
    (newIndex: number) => {
      if (externalCurrentIndex !== undefined) {
        onIndexChange?.(newIndex);
      } else {
        setInternalCurrentIndex(newIndex);
      }
    },
    [externalCurrentIndex, onIndexChange],
  );

  const measureItems = useCallback(() => {
    if (!containerRef.current || items.length === 0) return;

    const container = containerRef.current;
    const containerW = container.offsetWidth;

    const calculatedItemWidth = baseWidth;

    const itemsWithGap = calculatedItemWidth + gap;
    const maxItemsPerView = Math.floor((containerW + gap) / itemsWithGap);
    const finalItemsPerView = Math.max(1, Math.min(maxItemsPerView, items.length));

    setContainerWidth(containerW);
    setItemWidth(calculatedItemWidth);
    setItemsPerView(finalItemsPerView);
    setIsReady(true);
  }, [items.length, gap, baseWidth]);

  const extendedItems = useMemo(() => {
    if (!isAutoPlaying || items.length === 0) return items;

    const itemsToClone = Math.min(2, items.length);
    const startClones = items.slice(-itemsToClone);
    const endClones = items.slice(0, itemsToClone);

    return [...startClones, ...items, ...endClones];
  }, [items, isAutoPlaying]);

  const { maxIndex } = useMemo(() => {
    if (!isReady) return { maxIndex: 0, realStartIndex: 0 };

    if (isAutoPlaying) {
      const itemsToClone = Math.min(2, items.length);
      return {
        maxIndex: extendedItems.length - 1,
        realStartIndex: itemsToClone,
      };
    }

    return {
      maxIndex: Math.max(0, items.length - 1),
      realStartIndex: 0,
    };
  }, [isReady, items.length, isAutoPlaying, extendedItems.length]);

  const getXPosition = useCallback(
    (index: number) => {
      if (!isReady) return 0;

      const position = -index * trackItemOffset;

      const currentAlign = isMobile ? opts.mobileAlign : opts.align;
      if (currentAlign === 'center' && !isMobile) {
        const centerOffset = (containerWidth - itemWidth) / 2;
        return position + centerOffset;
      }

      return position;
    },
    [isReady, trackItemOffset, isMobile, opts.align, opts.mobileAlign, containerWidth, itemWidth],
  );

  const handleInfiniteLoop = useCallback(
    (newIndex: number) => {
      if (!isAutoPlaying) return newIndex;

      const itemsToClone = Math.min(2, items.length);
      const realItemsStart = itemsToClone;
      const realItemsEnd = itemsToClone + items.length;

      if (newIndex >= realItemsEnd) {
        setIsTransitioning(true);
        const targetX = getXPosition(realItemsStart);

        animate(x, targetX, {
          type: 'spring',
          stiffness: 250,
          damping: 20,
          mass: 1,
          duration: 0.8,
        }).then(() => {
          setCurrentIndex(realItemsStart);
          setIsTransitioning(false);
        });

        return newIndex;
      }

      if (newIndex < realItemsStart) {
        setIsTransitioning(true);
        const targetIndex = realItemsEnd - 1;
        const targetX = getXPosition(targetIndex);

        animate(x, targetX, {
          type: 'spring',
          stiffness: 250,
          damping: 20,
          mass: 1,
          duration: 0.8,
        }).then(() => {
          setCurrentIndex(targetIndex);
          setIsTransitioning(false);
        });

        return newIndex;
      }

      return newIndex;
    },
    [isAutoPlaying, items.length, getXPosition, x, setCurrentIndex],
  );

  const handleDragEnd = useCallback(
    (event: any, info: PanInfo) => {
      if (!opts.active || !animated || isTransitioning) return;

      if (isAutoPlaying) {
        setIsAutoPlaying(false);
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
          autoPlayRef.current = null;
        }
      }

      const threshold = 50;
      const velocity = info.velocity.x;
      const offset = info.offset.x;

      let newIndex = currentIndex;

      if (Math.abs(offset) > threshold || Math.abs(velocity) > 500) {
        if (offset > 0 || velocity > 500) {
          newIndex = Math.max(0, currentIndex - 1);
        } else {
          newIndex = Math.min(maxIndex, currentIndex + 1);
        }
      }

      if (newIndex === currentIndex && Math.abs(offset) > threshold) {
        const bounceDistance = 30;
        const bounceDirection = offset > 0 ? bounceDistance : -bounceDistance;
        const currentX = getXPosition(currentIndex);

        animate(x, currentX + bounceDirection, {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          mass: 0.5,
          duration: 0.3,
        }).then(() => {
          animate(x, currentX, {
            type: 'spring',
            stiffness: 300,
            damping: 25,
            mass: 0.8,
            duration: 0.4,
          });
        });
        return;
      }

      const finalIndex = handleInfiniteLoop(newIndex);
      if (finalIndex === newIndex) {
        setCurrentIndex(newIndex);
      }
    },
    [
      currentIndex,
      maxIndex,
      opts.active,
      animated,
      isAutoPlaying,
      isTransitioning,
      handleInfiniteLoop,
      getXPosition,
      x,
      setCurrentIndex,
    ],
  );

  useEffect(() => {
    if (!animated || !isReady || isTransitioning) return;

    const targetX = getXPosition(currentIndex);

    animate(x, targetX, {
      type: 'spring',
      stiffness: 280,
      damping: 22,
      mass: 0.9,
      duration: opts.duration || 0.6,
    });
  }, [currentIndex, getXPosition, x, animated, isReady, isTransitioning, opts.duration]);

  useEffect(() => {
    const measureContainer = () => {
      measureItems();
    };

    const timer = setTimeout(measureContainer, 100);
    window.addEventListener('resize', measureContainer);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measureContainer);
    };
  }, [measureItems]);

  useEffect(() => {
    if (isAutoPlaying && isReady && !isTransitioning) {
      const itemsToClone = Math.min(2, items.length);
      const initialIndex = itemsToClone + (opts.startIndex || 0);
      setCurrentIndex(initialIndex);
    }
  }, [isAutoPlaying, isReady, items.length, opts.startIndex, isTransitioning, setCurrentIndex]);

  useEffect(() => {
    if (!isAutoPlaying || !animated || !isReady || isTransitioning) return;

    const startAutoPlay = () => {
      autoPlayRef.current = setInterval(() => {
        const nextIndex = currentIndex + 1;
        handleInfiniteLoop(nextIndex);
        setCurrentIndex(nextIndex);
      }, 4000);
    };

    startAutoPlay();

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
    };
  }, [isAutoPlaying, animated, isReady, isTransitioning, handleInfiniteLoop, setCurrentIndex]);

  const itemsToRender = isAutoPlaying ? extendedItems : items;
  const range = useMemo(() => {
    return itemsToRender.map((_, index) => { return -(index + 1) * trackItemOffset; });
  }, [itemsToRender, trackItemOffset]);
  const outputRange = useMemo(() => {
    return range.map(() => { return (enable3D ? 45 : 0); });
  }, [range, enable3D]);
  const rotateY = useTransform(x, range, outputRange, { clamp: false });

  if (!isReady) {
    return (
      <div ref={containerRef} className="relative overflow-hidden opacity-0" style={{ width: `${baseWidth}px` }}>
        <div className="flex" style={{ gap: `${gap}px` }}>
          {items.map((item, index) => {
            return (
              <div
                key={`carousel-item-measure-${index}`}
                ref={(el) => {
                  itemsRef.current[index] = el;
                }}
                className="flex-shrink-0"
                style={{ width: `${itemWidth}px` }}
              >
                {item}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (animated) {
    return (
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={{
          width: `${baseWidth}px`,
          maskImage: blur ? 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' : 'none',
        }}
      >
        <motion.div
          className="flex"
          style={{
            x,
            gap: `${gap}px`,
            perspective: enable3D ? 1000 : 'none',
            perspectiveOrigin: `${currentIndex * trackItemOffset + itemWidth / 2}px 50%`,
          }}
          drag={opts.active && !isTransitioning ? 'x' : false}
          dragConstraints={{
            left: -((itemsToRender.length - 1) * trackItemOffset),
            right: 0,
          }}
          dragElastic={0.15}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          transition={{
            type: 'spring',
            stiffness: 280,
            damping: 22,
            mass: 0.9,
          }}
        >
          {itemsToRender.map((item, index) => {
            return (
              <motion.div
                key={`carousel-item-${isAutoPlaying ? 'extended' : 'normal'}-${index}`}
                className="flex-shrink-0 cursor-grab active:cursor-grabbing"
                style={{
                  width: `${itemWidth}px`,
                  rotateY: enable3D && Array.isArray(rotateY) ? String(rotateY[index] ?? 0) : '0',
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                  mass: 0.8,
                  delay: index * 0.05,
                }}
              >
                {item}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative overflow-hidden" style={{ width: `${baseWidth}px` }}>
      <div className="flex" style={{ gap: `${gap}px` }}>
        {items.map((item, index) => {
          return (
            <motion.div
              key={`carousel-item-static-${index}`}
              className="flex-shrink-0"
              style={{ width: `${itemWidth}px` }}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 250,
                damping: 18,
                mass: 1,
                delay: index * 0.1,
              }}
            >
              {item}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HCarousel3D;
